/**
 * Pre-calculated Frame Offset Data
 * 
 * Generated server-side by export_offset_map.py
 * Contains median pixel offsets (dx, dy) and scale factor (dz) for all frame pairs.
 * 
 * @see OFFSET_MAP_WITH_SCALE.md for full documentation
 */

export interface FrameOffset {
  dx: number        // Horizontal stitch offset in pixels (already inverted for stitching)
  dy: number        // Vertical stitch offset in pixels (already inverted for stitching)
  dz: number        // Scale factor change (-0.3 to +0.3 typical range)
  confidence: number // Quality score (0.5 to 1.0, based on variance)
  pointCount: number // Number of shared 3D points used in calculation
  distance_3d: number  // Euclidean distance √(dx² + dy² + dz²) - pre-computed during load
  angle_diff?: number   // Angular difference in degrees - computed for filtering
  yaw_diff?: number     // Yaw angle difference in degrees
  pitch_diff?: number   // Pitch angle difference in degrees
  roll_diff?: number    // Roll angle difference in degrees
  similar_orientation?: boolean  // Whether frames have similar orientation
  motion_type?: string  // Type of motion (e.g., "pan", "tilt", "zoom")
}

export interface OffsetMap {
  metadata: {
    frameCount: number
    sceneCenter: [number, number, number]
    generated: string
    version: string
  }
  all_pairs: Record<string, FrameOffset>  // ALL frame pairs with shared points: "0_1", "1_36", "5_100", etc.
  
  // Backward compatibility (legacy)
  sequential?: Record<string, FrameOffset>  // "0_1", "1_0", "1_2", "2_1", etc.
  // jumps removed - only use all_pairs (COLMAP-validated connections)
  
  // NEW: Connectivity graph from point cloud observations
  connectivity?: Record<string, number[]>              // "0": [1, 5, 12, 23, ...]
  transitiveConnectivity?: Record<string, number[]>    // "0": [0, 1, 2, ..., N]
  components?: Record<string, number>                  // "0": 0 (component ID)
  
  // 🆕 Phase Correlation Extended Pairs (BFS-based transitive pairs)
  extended_pairs?: Record<string, FrameOffset>        // Transitive pairs with spatial validation
  extended_connectivity?: Record<string | number, number[]>  // Extended reachability graph
  extended_pairs_metadata?: {
    max_depth: number
    max_deviation_ratio: number
    validation_stats: {
      total_extended_pairs: number
      total_sequential_pairs: number
      total_rejected_pairs: number
      total_discontinuities: number
      avg_deviation_ratio: number
      depth_distribution: Record<string, number>
      frames_with_extensions: number
    }
  }
}

/**
 * Manifest frame offset (compressed for smaller payload)
 * Used in manifest.json frames[N].offsets
 * 
 * STORAGE OPTIMIZATION: Only stores forward offsets (to_id > from_id)
 * Reverse offsets are regenerated client-side by negating dx/dy
 * This reduces offset storage by ~50% with O(1) lookup cost
 */
export interface ManifestFrameOffset {
  dx: number        // int16: horizontal offset in pixels (rounded to nearest integer)
  dy: number        // int16: vertical offset in pixels (rounded to nearest integer)
  dist: number      // uint16: compressed distance_3d (scale: 1/64)
                    // Decode: actual_distance = dist / 64
                    // Precision: ±0.015 pixels (imperceptible)
}

/**
 * Manifest frame data structure
 * Includes both static metadata and optimized offset storage
 */
export interface ManifestFrame {
  id: number
  frame_file: string
  neighbors: number[]                        // All connected frame IDs (bidirectional)
  offsets: Record<number, ManifestFrameOffset>  // Only forward offsets (to_id > from_id)
                                             // Reverse offsets: negate dx/dy of stored offset
}

/**
 * Build lookup key for offset map
 * @example getOffsetKey(0, 1) → "0_1"
 */
export function getOffsetKey(fromFrame: number, toFrame: number): string {
  return `${fromFrame}_${toFrame}`
}

/**
 * Calculate distance in 3D space from pixel offsets
 * Approximates 3D camera distance using pixel shifts and scale change
 * @param offset Frame offset with dx, dy, dz values
 * @returns Distance in normalized 3D units
 */
export function calculateDistance3D(offset: FrameOffset): number {
  // Combine pixel offsets (scaled down) with z scale change
  // √(dx² + dy² + dz²) where values are normalized to same scale
  const pixelScaleFactor = 0.001  // Convert pixels to normalized units
  const scaledDx = offset.dx * pixelScaleFactor
  const scaledDy = offset.dy * pixelScaleFactor
  const scaledDz = offset.dz * 10  // Scale up dz since it's already small (typically -0.3 to +0.3)
  
  return Math.sqrt(scaledDx * scaledDx + scaledDy * scaledDy + scaledDz * scaledDz)
}

/**
 * Calculate angular difference between pan direction and offset
 * Uses atan2 to find angle of offset vector and compares with pan angle
 * @param offset Frame offset with dx, dy values
 * @param panAngleDegrees Current pan direction in degrees
 * @returns Angular difference in degrees (0-180)
 */
export function calculateAngleDiff(offset: FrameOffset, panAngleDegrees: number = 0): number {
  // Calculate angle of offset vector in degrees
  const offsetAngle = Math.atan2(offset.dy, offset.dx) * (180 / Math.PI)
  
  // Normalize angles to 0-360
  const normalizedPan = panAngleDegrees % 360
  const normalizedOffset = offsetAngle % 360
  
  // Calculate shortest angular distance
  let diff = Math.abs(normalizedOffset - normalizedPan)
  if (diff > 180) {
    diff = 360 - diff
  }
  
  return diff
}

/**
 * Parse offset key back to frame indices
 * @example parseOffsetKey("5_6") → [5, 6]
 */
export function parseOffsetKey(key: string): [number, number] {
  const [from, to] = key.split('_').map(Number)
  return [from, to]
}

/**
 * Get offset between two frames with bidirectional lookup
 * 
 * OPTIMIZATION: Manifest stores only forward offsets (to_id > from_id)
 * This function handles both directions by negating dx/dy for reverse lookups
 * 
 * @param fromFrame Source frame ID
 * @param toFrame Target frame ID
 * @param manifest Manifest data with optimized one-direction storage
 * @returns Offset object, or null if no connection exists
 * 
 * Example:
 *   getManifestOffset(0, 1, manifest)   // Direct lookup (1 > 0)
 *   getManifestOffset(1, 0, manifest)   // Reverse lookup: negate stored 0→1
 */
export function getManifestOffset(
  fromFrame: number,
  toFrame: number,
  manifest: { frames: ManifestFrame[] }
): ManifestFrameOffset | null {
  if (fromFrame >= manifest.frames.length || toFrame >= manifest.frames.length) {
    return null
  }

  // Determine which direction is stored (to_id > from_id)
  if (fromFrame < toFrame) {
    // Forward direction: look in fromFrame's offsets
    return manifest.frames[fromFrame].offsets[toFrame] || null
  } else if (toFrame < fromFrame) {
    // Reverse direction: look in toFrame's offsets and negate
    const stored = manifest.frames[toFrame].offsets[fromFrame]
    if (!stored) return null
    
    return {
      dx: -stored.dx,
      dy: -stored.dy,
      dist: stored.dist  // distance is always positive
    }
  }
  
  return null
}

/**
 * Get offset from map with fallback
 */
export function getOffset(
  offsetMap: OffsetMap | null,
  fromFrame: number,
  toFrame: number
): FrameOffset | null {
  if (!offsetMap) return null
  
  const key = getOffsetKey(fromFrame, toFrame)
  return offsetMap.all_pairs[key] || offsetMap.sequential?.[key] || null
}
